import React, { Requireable } from 'react';
import PropTypes from 'prop-types';
import isTwelveHourTime from './lib/is-twelve-hour-time';
import replaceCharAt from './lib/replace-char-at';
import getGroupId from './lib/get-group-id';
import getGroups from './lib/get-groups';
import adder from './lib/time-string-adder';
import caret from './lib/caret';
import validate from './lib/validate';
import validateDuration from './lib/validate-duration';
import styled from 'styled-components';

const SILHOUETTE = '00:00:00:000 AM';

// Imported from https://github.com/radumardale/react-keyboard-time-input

// isSeparator :: Char -> Bool
const isSeparator = (char) => /[:\s]/.test(char);

const Wrapper = styled.div`
    display: flex;
    width: 60px;
    grid-gap: 2px;
    align-items: center;
    justify-content: space-between;
    input {
        width: 45px;
    }

    svg {
        color: rgba(0, 0, 0, 0.25);
        height: 16px;
    }
`;

class TimeInput2 extends React.Component<
    { className; value; onChange; isDuration },
    { input?: HTMLInputElement; caretIndex?: number; mounted: boolean }
> {
    inputRef: React.RefObject<HTMLInputElement>;
    constructor(props) {
        super(props);
        this.state = { mounted: false };
        this.inputRef = React.createRef();
    }
    static propTypes: {
        className: Requireable<string>;
        value: Requireable<string>;
        onChange: Requireable<(...args: any[]) => void>;
        isDuration: Requireable<boolean>;
    };
    static defaultProps: {
        value: string;
    };

    componentDidMount() {
        this.setState({ ...this.state, mounted: true });
    }

    componentWillUnmount() {
        this.setState({ ...this.state, mounted: false });
    }

    render() {
        let className = 'time';
        if (this.props.className) {
            className += ' ' + this.props.className;
        }

        const handleClick = (event: any) => {
            event.target.select();
        };

        return (
            <Wrapper className={className}>
                <input
                    className="time-input"
                    ref={this.inputRef}
                    type="text"
                    value={this.format(this.props.value)}
                    onChange={this.handleChange}
                    onBlur={this.handleBlur}
                    onKeyDown={this.handleKeyDown}
                    onClick={handleClick}
                />
                <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fas"
                    data-icon="clock"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                >
                    <path
                        fill="currentColor"
                        d="M256,8C119,8,8,119,8,256S119,504,256,504,504,393,504,256,393,8,256,8Zm92.49,313h0l-20,25a16,16,0,0,1-22.49,2.5h0l-67-49.72a40,40,0,0,1-15-31.23V112a16,16,0,0,1,16-16h32a16,16,0,0,1,16,16V256l58,42.5A16,16,0,0,1,348.49,321Z"
                    />
                </svg>
            </Wrapper>
        );
    }

    format = (val) => {
        if (isTwelveHourTime(val)) val = val.replace(/^00/, '12');
        return val.toUpperCase();
    };

    componentDidUpdate() {
        const index = this.state.caretIndex;
        if (this.inputRef?.current && (index || index === 0)) {
            caret.set(this.inputRef.current, index);
        }
        if (this.inputRef?.current && this.inputRef.current !== this.state.input) {
            this.setState({ ...this.state, input: this.inputRef.current });
        }
    }

    handleBlur = () => {
        if (this.state.mounted) this.setState({ caretIndex: null });
    };

    handleEscape = () => {
        if (this.state.mounted) this.state.input.blur();
    };

    handleTab = (event) => {
        const start = caret.start(this.state.input);
        const value = this.props.value;
        const groups = getGroups(value);
        let groupId = getGroupId(start);
        if (event.shiftKey) {
            if (!groupId) return;
            groupId--;
        } else {
            if (groupId >= groups.length - 1) return;
            groupId++;
        }
        event.preventDefault();
        let index = groupId * 3;
        if (this.props.value.charAt(index) === ' ') index++;
        if (this.state.mounted) this.setState({ caretIndex: index });
    };

    handleArrows = (event) => {
        event.preventDefault();
        const start = caret.start(this.state.input);
        let value = this.props.value;
        let amount = event.which === 38 ? 1 : -1;
        if (event.shiftKey) {
            amount *= 2;
            if (event.metaKey) amount *= 2;
        }
        value = adder(value, getGroupId(start), amount);
        this.onChange(value, start);
    };

    silhouette = () => {
        return this.props.value.replace(/\d/g, (val, i) => SILHOUETTE.charAt(i));
    };

    handleBackspace = (event) => {
        event.preventDefault();
        let start = caret.start(this.state.input);
        let value = this.props.value;
        let end = caret.end(this.state.input);
        if (!start && !end) return;
        let diff = end - start;
        const silhouette = this.silhouette();
        if (!diff) {
            if (value[start - 1] === ':') start--;
            value = replaceCharAt(value, start - 1, silhouette.charAt(start - 1));
            start--;
        } else {
            while (diff--) {
                if (value[end - 1] !== ':') {
                    value = replaceCharAt(value, end - 1, silhouette.charAt(end - 1));
                }
                end--;
            }
        }
        if (value.charAt(start - 1) === ':') start--;
        this.onChange(value, start);
    };

    handleForwardSpace = (event) => {
        event.preventDefault();
        let start = caret.start(this.state.input);
        let value = this.props.value;
        const end = caret.end(this.state.input);
        if ((start === end ? 1 : 0) === value.length - 1) return;
        let diff = end - start;
        const silhouette = this.silhouette();
        if (!diff) {
            if (value[start] === ':') start++;
            value = replaceCharAt(value, start, silhouette.charAt(start));
            start++;
        } else {
            while (diff--) {
                if (value[end - 1] !== ':') {
                    value = replaceCharAt(value, start, silhouette.charAt(start));
                }
                start++;
            }
        }
        if (value.charAt(start) === ':') start++;
        this.onChange(value, start);
    };

    handleKeyDown = (event) => {
        switch (event.which) {
            case 9: // Tab
                return this.handleTab(event);
            case 8: // Backspace
                return this.handleBackspace(event);
            case 46: // Forward
                return this.handleForwardSpace(event);
            case 27: // Esc
                return this.handleEscape();
            case 38: // Left
            case 40: // Right
                return this.handleArrows(event);
            default:
                break;
        }
    };

    handleChange = (event) => {
        let value = this.props.value;
        let newValue = this.state.input.value;
        let diff = newValue.length - value.length;
        let end = caret.start(this.state.input);
        let insertion;
        let start = end - Math.abs(diff);
        event.preventDefault();
        if (diff > 0) {
            insertion = newValue.slice(end - diff, end);
            while (diff--) {
                const oldChar = value.charAt(start);
                const newChar = insertion.charAt(0);
                if (isSeparator(oldChar)) {
                    if (isSeparator(newChar)) {
                        insertion = insertion.slice(1);
                        start++;
                    } else {
                        start++;
                        diff++;
                        end++;
                    }
                } else {
                    value = replaceCharAt(value, start, newChar);
                    insertion = insertion.slice(1);
                    start++;
                }
            }
            newValue = value;
        } else {
            if (newValue.charAt(start) === ':') start++;
            // apply default to selection
            let result = value;
            for (let i = start; i < end; i++) {
                result = replaceCharAt(result, i, newValue.charAt(i));
            }
            newValue = result;
        }

        let isValid = false;
        if (this.props.isDuration) {
            if (validateDuration(newValue)) {
                if (newValue.charAt(end) === ':') end++;
                this.onChange(newValue, end);
                isValid = true;
            }
        } else {
            if (validate(newValue)) {
                if (newValue.charAt(end) === ':') end++;
                this.onChange(newValue, end);
                isValid = true;
            }
        }
        if (!isValid) {
            const caretIndex = this.props.value.length - (newValue.length - end);
            if (this.state.mounted) this.setState({ caretIndex: caretIndex });
        }
    };

    onChange = (str, caretIndex) => {
        if (this.props.onChange) this.props.onChange(this.format(str));
        if (this.state.mounted && typeof caretIndex === 'number') this.setState({ caretIndex: caretIndex });
    };
}

TimeInput2.defaultProps = {
    value: '12:00',
};

TimeInput2.propTypes = {
    className: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    isDuration: PropTypes.bool,
};

export default TimeInput2;
